import * as React from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react';

import { getImageFromProductByVariant } from '../../helpers/getImageFromProductByVariant';
import { Button, Svg, SizeBox, BuyButton } from '../modules';
import { buildProductLink } from '../../pages/ProductDetail/ProductDetailPage';
import { CurrencyFormatter as Currency } from '../../utils/utils';
import Form from '../../components/FormGenerator/Form';
import QuantityForm from '../Form/Quantity';
import CartService from '../../services/CartService';
import Image from '../../components/Image';
import ProductTags from '../ProductTags';
import CountryService from '../../services/CountryService';
import {
  canProductBuy,
  canProductInquiry,
  canDisplayProductPrice,
  getAttributeDefinitions,
  displayAttributeDefinitions,
} from '../../helpers/products';
import InquiryButton from '../InquiryButton/InquiryButton';
import { findFinalPrice } from '../../helpers/prices';
import { getPriceWithoutVat } from 'data-layer/src/helpers/prices/getPriceWithoutVat';
import { vatDescription } from '../../helpers/vatDescription';
import { showWatchDogButtonAvailabilityFlag } from 'helpers/src/availability/watchDogActions';
import GoogleTagManagerService from '../../services/GoogleTagManagerService';

interface IProductBoxProps {
  boxType: string;

  orderItem?: any;

  variant?: GQL.IProductVariant;
  quantity?: number;
  size?: string;
  sizes?: Array<string>;

  tags?: GQL.IProductTag[];
  hideTags?: string[];

  language?: string;
  category?: GQL.ICategory;
  onChange?: (newVal: number) => void;
  onRemove?: () => void;
  onToggleFavorite?: (variantId: string) => void;

  image?: GQL.IImage | null;
  isFavorite?: boolean;

  hideFavorite?: boolean;

  attributes?: Array<GQL.IAttribute>;

  currency?: GQL.ICurrency;
  setCurrentProductAvailabityWatchdogForm?: (product?: GQL.IProduct, variant?: GQL.IProductVariant) => void;
  GTMItemListName?: string;
}

interface IProductBoxState {
  formFields: any[];
  formValues: any;
}

@observer
class ProductBox extends React.Component<IProductBoxProps> {
  public state: IProductBoxState;
  private form: any;

  constructor(props: IProductBoxProps) {
    super(props);

    this.state = {
      formFields: [
        {
          fieldName: 'Size',
          isRequired: true,
          label: 'Velikost',
          type: 'select',
          options: [
            {
              label: 'M',
              value: 'M',
            },
            {
              label: 'S',
              value: 'S',
            },
          ],
        },
      ],
      formValues: {},
    };
  }

  public handleChange(value: any, field: string) {
    this.setState({ formValues: { ...this.state.formValues, [field]: value } });
  }

  public setFormState(fields: any) {
    this.setState({ fields });
  }

  public handleSend() {
    //
  }

  public render() {
    const {
      orderItem,
      variant,
      quantity,
      boxType,
      size,
      sizes,
      tags,
      hideTags,
      category,
      attributes,
      currency,
      GTMItemListName,
    } = this.props;
    const language = CountryService.GetInstance().language || 'cs';

    if (!variant && boxType !== 'order') {
      return null;
    }

    let image = null;
    let price = null;
    let priceWith = null;
    let recommendedPrice = null;

    let attributeDefinition = null;

    // DPH vybrané země
    const { VATrate } = CountryService.GetInstance();

    // produkt z varianty
    const product = variant ? (variant.product as GQL.IProduct) : null;
    const watchDogButtonFor =
      'string' === typeof process.env.REACT_APP_SHOW_AVAILABILITY_WATCH_FORM_FOR_PRODUCTS_WITH_AVAILABILITYIDS
        ? process.env.REACT_APP_SHOW_AVAILABILITY_WATCH_FORM_FOR_PRODUCTS_WITH_AVAILABILITYIDS.split(',').map(
            a => a.trim(),
          )
        : [];

    const showWatchDogButton =
      product &&
      product.availability &&
      'string' === typeof product.availability.id
        ? 0 <= watchDogButtonFor.indexOf(product.availability.id) ||
          showWatchDogButtonAvailabilityFlag ===
            (product.availability as any).watchDogAction
        : false;

        // v klasickém katalogu
    if (variant && product) {
      image = this.props.image || getImageFromProductByVariant(product, variant as GQL.IProductVariant);

      // cena
      price = findFinalPrice(variant, false);
      priceWith = findFinalPrice(variant, false, VATrate);

      // běžná cena bez DPH
      recommendedPrice = findFinalPrice(variant, true, 0);

      // atributy varianty
      attributeDefinition = getAttributeDefinitions(variant, attributes || [], language);
    }

    // údaje z elementu OrderItem
    // v tomto případě není k dispozici klasicka varianta apod.
    if (boxType === 'order' && orderItem) {
      // obrázek a cena
      image = orderItem.image;
      price = orderItem.price;

      // atributy položky
      attributeDefinition = getAttributeDefinitions(orderItem, attributes || [], language);
    }

    switch (boxType) {
      case 'order':
        if (!orderItem) {
          return null;
        }

        return (
          <div className={'productBox' + (boxType ? ' productBox--' + boxType : '')}>
            <div className="productBox__inner">
              <div className="productBox__preview">
                <Image image={image} className="productBox__img" alt={orderItem.name[language]} />
              </div>
              <div className="productBox__info">
                <div className="productBox__title">
                  {orderItem.name[language] ? orderItem.name[language] : orderItem.title}
                  <br />
                  <span>
                    {(('string' === orderItem.ean) && (orderItem.ean.length > 0)) ? <div className="productBox__code">Item number: #{orderItem.ean}</div> : ''}
                    {attributeDefinition && (
                      <div className="productBox__definitions">
                        {displayAttributeDefinitions(attributeDefinition)}
                      </div>
                    )}
                  </span>
                </div>

                <div className="row">
                  <div className="col-md-6">
                    <div className="productBox__properties text-right">
                      <span className="productBox__property">
                        <b>{quantity}</b> pcs
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="productBox__price">
                      <div>
                        <div>
                          <Currency
                            price={getPriceWithoutVat(price * (quantity || 1), orderItem.VATrate)}
                            decimals={2}
                            currency={currency || undefined}
                          />
                        </div>
                      </div>

                      {orderItem.VATrate > 0 && (
                        <span className="withoutVat">
                          <Currency
                            price={price * (quantity || 1)}
                            freeAsWord={false}
                            currency={currency || undefined}
                          />
                          {' '}
                          incl. {vatDescription()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'cart':
        if (!variant || !product) {
          return null;
        }

        return (
          <div className={'productBox' + (boxType ? ' productBox--' + boxType : '')}>
            <div className="productBox__inner">
              <div className="productBox__preview">
                <Image image={image} className="productBox__img" alt={product.name[language]} />
              </div>
              <div className="productBox__info">
                <div className="productBox__title">
                  <Link to={buildProductLink(product, undefined, variant, language) || ''}>
                    {product.name[language]}
                  </Link>
                  <br />
                  <span>
                    <div className="productBox__code">Item number: #{variant.sku}</div>
                    {attributeDefinition && (
                      <div className="productBox__definitions">
                        {displayAttributeDefinitions(attributeDefinition)}
                      </div>
                    )}
                  </span>
                </div>

                <div className="row">
                  <div className="col-lg-4">
                    <div className="productBox__properties text-right">
                      <span className="productBox__property">
                        <b>{quantity}</b> pcs
                      </span>
                    </div>
                  </div>
                  <div className="col-lg-8">
                    <div className="productBox__price">
                      <div>
                        <div>
                          <Currency
                              price={CartService.GetInstance().getTotalPriceWithoutVatForVariant(
                                variant.id,
                                true,
                              )}
                              decimals={2}
                            />
                          
                        </div>
                        {CartService.GetInstance().getTotalPriceForVariant(variant.id, true) !==
                          CartService.GetInstance().getTotalPriceForVariant(variant.id, false) && (
                            <Currency
                              style={{
                                textDecoration: 'line-through',
                                fontWeight: 'normal',
                                fontSize: '0.75em',
                                color: 'gray',
                              }}
                              price={CartService.GetInstance().getTotalPriceForVariant(variant.id, false)}
                            />
                        )}
                        {VATrate > 0 && (
                          <span className="withoutVat">
                            <Currency
                              price={CartService.GetInstance().getTotalPriceForVariant(variant.id, true)}
                              freeAsWord={false}
                              currency={currency || undefined}
                            />
                            {' '}
                            incl. {vatDescription()}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'favorite':
        if (!variant || !product) {
          return null;
        }

        return (
          <div className={'productBox' + (boxType ? ' productBox--' + boxType : '')}>
            {boxType === 'favorite' && (
              <ProductTags language={language} variant={variant} hideTags={hideTags} />
            )}

            <div className="productBox__inner">
              <div className="productBox__preview">
                <Image image={image} className="productBox__img" alt={product.name[language]} />
              </div>
              <div className="productBox__info">
                <div className="productBox__title">
                  {/* // todo: dořešit odkaz na produkt */}
                  {/* <Link to={``}> */}
                  {product.name[language]}
                  {/* </Link> */}
                </div>

                {boxType === 'favorite' ? (
                  <React.Fragment>
                    <div className="productBox__inline">
                      <Form
                        fields={this.state.formFields}
                        values={this.state.formValues}
                        handleChange={this.handleChange}
                      >
                        {(form: any) => {
                          this.form = form;
                        }}
                      </Form>
                      <div className="productBox__price">
                        <Currency price={price || 0} currency={currency || undefined} />
                      </div>
                      <button
                        onClick={() => (this.props.onRemove ? this.props.onRemove() : undefined)}
                        className="productBox__remove"
                      />
                    </div>
                    <Button type="toCartSm">Do košíku</Button>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {this.props.onRemove && <button className="productBox__remove" />}

                    <div className="row">
                      {boxType === 'cart' ? (
                        <React.Fragment>
                          {this.props.onRemove && (
                            <button
                              onClick={() => (this.props.onRemove ? this.props.onRemove() : undefined)}
                              className="productBox__remove"
                            />
                          )}
                          <div className="productBox__inline">
                            <QuantityForm
                              onChange={newVal =>
                                this.props.onChange ? this.props.onChange(newVal) : undefined
                              }
                              value={quantity}
                            />
                            <div className="productBox__price">
                              <Currency
                                price={CartService.GetInstance().getTotalPriceForVariant(variant.id, true)}
                                currency={currency || undefined}
                              />

                              {CartService.GetInstance().getTotalPriceForVariant(variant.id, true) !==
                                CartService.GetInstance().getTotalPriceForVariant(variant.id, false) && (
                                <Currency
                                  style={{
                                    textDecoration: 'line-through',
                                    fontSize: '0.75em',
                                    color: 'gray',
                                  }}
                                  bold={false}
                                  price={CartService.GetInstance().getTotalPriceForVariant(variant.id, false)}
                                  currency={currency || undefined}
                                />
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      ) : (
                        <div className="col-md-6">
                          <div className="productBox__price">
                            <Currency
                              price={CartService.GetInstance().getTotalPriceForVariant(variant.id, true)}
                              currency={currency || undefined}
                            />
                            {CartService.GetInstance().getTotalPriceForVariant(variant.id, true) !==
                              CartService.GetInstance().getTotalPriceForVariant(variant.id, false) && (
                              <Currency
                                style={{
                                  textDecoration: 'line-through',
                                  fontSize: '0.75em',
                                  color: 'gray',
                                }}
                                bold={false}
                                price={CartService.GetInstance().getTotalPriceForVariant(variant.id, false)}
                                currency={currency || undefined}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
        );

      case 'modal':
        if (!variant || !product) {
          return null;
        }

        return (
          <div className={'productBox' + (boxType ? ' productBox--' + boxType : '')}>
            <div className="productBox__preview">
              <Image
                image={image}
                width={152}
                height={152}
                className="productBox__img"
                alt={product.name[language]}
              />
            </div>
            <div className="productBox__info">
              <div className="productBox__title">
                {product.name[language]}
                <div className="productBox__code">Item number: #{variant.ean}</div>
                {attributeDefinition && (
                  <div className="productBox__definitions">
                    {displayAttributeDefinitions(attributeDefinition)}
                  </div>
                )}
              </div>
              <div className="productBox__quantity">{quantity && `${quantity} pcs`}</div>
              <div className="productBox__price">
                <Currency price={price || 0} currency={currency || undefined} />
              </div>
            </div>
          </div>
        );

      case 'catalog':
      case 'buy':
      default:
        if (!variant || !product) {
          return null;
        }

        const productLink = buildProductLink(product, category, variant as GQL.IProductVariant, language) || '';
        const variantsLength = product.variants ? product.variants.length : 0;

        return (
          <div
            className={
              'productBox' +
              (boxType ? ' productBox--' + boxType : '') +
              (this.props.isFavorite ? ' isFavorite ' : '') +
              (sizes ? ' has-sizes' : '')
            }
          >
            <Link
              to={{
                pathname: productLink.split('?')[0],
                search: productLink.split('?')[1],
                state: {
                  GTMItemListName,
                }
              }}
              className="block"
              onClick={() => {
                if ('buy' === boxType) {
                  GoogleTagManagerService.clickProduct(variant, GTMItemListName);
                }
              }}
            >
              <ProductTags language={language} variant={variant} hideTags={hideTags} />
              {(this.props.hideFavorite == undefined || !this.props.hideFavorite) && (
                <button onClick={() => this.toggleFavorite(variant.id)} className="productBox__favorite">
                  <Svg name="heart" type="stroke" />
                </button>
              )}
              <div className="productBox__image d-flex align-items-center">
                <Image image={image} className="productBox__img catalog" alt={product.name[language]} width={519} height={519} /> {/* Do 18. 12. 2023 bylo width={320} height={320} */}
              </div>

              <div className="productBox__description">
                <span>IN: #{variant.sku}</span>
              </div>

              <div className="productBox__title">
                <span>{product.name[language]}</span>
              </div>
            </Link>

            <div className="productBox__bottom">
              <div className="productBox__price">
                {canDisplayProductPrice(product) && (
                  <>
                    {!!recommendedPrice && !!priceWith && recommendedPrice > price && (
                      <span className="recommended">
                        <Currency price={recommendedPrice || 0} />
                      </span>
                    )}
                    {VATrate > 0 && (
                      <React.Fragment>
                        <Currency price={price || 0} decimals={2} />
                        <br />
                        <span className="withoutVat">
                          <Currency price={priceWith || 0} currency={currency || undefined} /> incl. {vatDescription()}
                        </span>
                      </React.Fragment>
                    )}
                    {(!VATrate || VATrate <= 0) && (
                      <Currency price={price || 0} currency={currency || undefined} />
                    )}
                  </>
                )}
              </div>

              {sizes && (
                <div className="productBox__sizes">
                  {sizes.map((item: any, i: number) => (
                    <SizeBox key={i} size={item} />
                  ))}
                </div>
              )}
            </div>
            {boxType === 'buy' && canProductBuy(product) && (!showWatchDogButton || variantsLength > 1) && (
              <BuyButton product={product} variant={variant} productLink={productLink} GTMItemListName={this.props.GTMItemListName} />
            )}
            {boxType === 'buy' && canProductInquiry(product) && (
              <InquiryButton product={product} variant={variant} redirectToDetail={true} />
            )}
          </div>
        );
    }
  }

  private toggleFavorite(variantId: string) {
    if (this.props.onToggleFavorite) {
      this.props.onToggleFavorite(variantId);
    }
  }
}

export default ProductBox;



// WEBPACK FOOTER //
// ./src/modules/ProductBox/ProductBox.tsx